import { EventEmitter, Injector, Optional } from "@angular/core";
import { Observable, Subject, Subscription, of } from "rxjs";
import { mergeMap, finalize } from "rxjs/operators";
import { FFileUtils } from "../utils/index";
import {
    UploadFile,
    UploadOutput,
    UploadInput,
    UploadStatus,
    BlobFile,
    UploadServerService,
    UploaderOptions,
} from "../model/index";
import { LocaleService } from "@farris/ui-locale";

export class FFileUploaderService {
    queue: UploadFile[];
    serviceEvents: EventEmitter<UploadOutput>;
    uploadScheduler: Subject<{
        file: UploadFile;
        event: UploadInput;
        files?: UploadFile[];
    }>;
    subs: { id: string; sub: Subscription; ids?: string[] }[];
    allowedContentTypes: string[] = ["*"];
    //默认不限制,0代表不限制 Number.POSITIVE_INFINITY
    maxUploads: number = 0;
    /** 单位M，默认是12M,0代表不限制 */
    maxFileSize: number = 12;
    concurrency: number = Number.POSITIVE_INFINITY;
    uploadedCount: number = 0;
    // 不允许名称中包含特殊字符
    notAllowedCharsInFileName=[];
    private extendServerConfig = null;
    private hasLocalSer=false;
    constructor(@Optional() private uploadServerSer: UploadServerService,@Optional() private inject:Injector ) {
        this.queue = [];
        this.serviceEvents = new EventEmitter<UploadOutput>();
        // 看是否多语言服务已更新
        this.hasLocalSer=FFileUtils.getLocalState(null);
        // 为多语言服务赋值
        if(this.inject&&!this.hasLocalSer){
           this.hasLocalSer=FFileUtils.getLocalState(this.inject.get(LocaleService));
        }
        this.uploadScheduler = new Subject();
        this.subs = [];      
        this.uploadScheduler
            .pipe(
                mergeMap((upload) => {
                    return this.serverMethod(upload);
                }, this.concurrency)
            )
            .subscribe((uploadOutput) => {
                // if (uploadOutput.type == 'removed' || uploadOutput.type == 'done') {
                //   // 删除或者done移除
                //   const subIndex = this.subs.findIndex(sub => sub.id === uploadOutput.file.id);
                //   if (subIndex > -1 && this.subs[subIndex].sub) {
                //     this.subs[subIndex].sub.unsubscribe();
                //   }
                //   this.subs.splice(subIndex, 1);
                // }
                if (uploadOutput.type == "removed") {
                    // 事件中返回的都是
                    this.queue = this.queue.filter(
                        (item) => item.progress.status !== UploadStatus.Remove
                    );
                    if (!uploadOutput.hasOwnProperty("message")) {
                        uploadOutput["message"] =FFileUtils.getLocalStr("uploadService.removed");
                    }
                }
                if (uploadOutput.type == "error") {
                    // 上传失败的附件移除
                    this.queue = this.queue.filter((queueItem) => {
                        return (
                            uploadOutput.files.findIndex((item) => queueItem.id == item.id) <
                            0
                        );
                    });
                }
                this.serviceEvents.emit(uploadOutput);
            });
    }
    setOptions(options: UploaderOptions) {
        // 重置文件大小、类型、个数限制
        if (options) {
            for (let prop in options) {
                this[prop] = options[prop];
            }
        }
    }
    handleFiles(incomingFiles: FileList): void {
        const allowedIncomingFiles: File[] = [].reduce.call(
            incomingFiles,
            (acc: File[], checkFile: File, i: number) => {
                const futureQueueLength = acc.length + this.queue.length + 1;
                let judgeResult = this.rejectedReason(
                    checkFile.name,
                    checkFile.type,
                    futureQueueLength,
                    checkFile.size
                );
                if (judgeResult.allowed) {
                    acc = acc.concat(checkFile);
                } else {
                    // 不符合当前文件类型或者内容超出限制，抛出事件
                    const rejectedFile: UploadFile = FFileUtils.makeUploadFile(
                        checkFile,
                        i
                    );
                    this.serviceEvents.emit({
                        type: "rejected",
                        file: rejectedFile,
                        message: judgeResult.message,
                    });
                }
                return acc;
            },
            []
        );

        // 构造文件结构，并单个抛出事件
        [].map.call(allowedIncomingFiles, (file: File, i: number) => {
            const uploadFile: UploadFile = FFileUtils.makeUploadFile(file, i);
            this.queue.push(uploadFile);
            this.serviceEvents.emit({ type: "addedToQueue", file: uploadFile });
        });

        // 所有的文件都已经添加，抛出事件
        this.serviceEvents.emit({ type: "allAddedToQueue" });
    }
    private rejectedReason(name, type, queuelength, size) {
        let allowed = false;
        let message = "";
        // 已存在同名文件
        let findDuplicateIndex = this.queue.findIndex((file) => file.name == name);
        if (findDuplicateIndex > -1) {
            message =FFileUtils.getLocalStr("uploadService.failedSameName");
        } else if (!this.isContentTypeAllowed(name)) {
            message =FFileUtils.getLocalStr("uploadService.failedType",[this.allowedContentTypes.join(",")]);
        } else if(!this.isNameAllowed(name)){
            let notAllowedLen=this.notAllowedCharsInFileName.length;
            let tMssage='';
            this.notAllowedCharsInFileName.map((notAllowItem,curIndex)=>{
                tMssage +=`<b style="display:inline-block;margin:0 4px;min-width:8px;text-align: center;">${notAllowItem?notAllowItem:"&nbsp;"}</b>`;
                if(curIndex!=notAllowedLen-1){
                    tMssage+=","
                }
            });
            message=FFileUtils.getLocalStr("uploadService.failedName",[tMssage]);
        }
        else if (
            this.maxUploads > 0 &&
            (this.maxUploads <= this.uploadedCount ||
                queuelength + this.uploadedCount > this.maxUploads)
        ) {
            message =FFileUtils.getLocalStr("uploadService.failedCount",[this.maxUploads+'']);
        } else if (!this.isFileSizeAllowed(size)) {
            message =FFileUtils.getLocalStr("uploadService.failedSize",[this.maxFileSize+'']);
        } else if (size == 0) {
            message =FFileUtils.getLocalStr("uploadService.failedEmpty");
        } else {
            allowed = true;
        }
        return {
            allowed,
            message,
        };
    }
    /**
     * 从前端传来事件，进行服务器端方法类型判断
     * @param input
     */
    initInputEvents(input: EventEmitter<UploadInput>): Subscription {
        return input.subscribe((event: UploadInput) => {
            switch (event.type) {
                case 'sliceUpload':
                    const _files = this.queue.filter(
                        (file) => file.progress.status === UploadStatus.Queue && (!event.file || file.fileIndex === event.file.fileIndex )
                    );

                    if (_files.length > 0) {
                        this.uploadScheduler.next({
                            files: _files,
                            event: event,
                            file: null,
                        });
                    }
                    break;
                case "upload":
                case "multipartUpload":
                    const uploadFileIndex = this.queue.findIndex(
                        (file) => file === event.file
                    );
                    if (uploadFileIndex !== -1 && event.file) {
                        this.uploadScheduler.next({
                            files: [this.queue[uploadFileIndex]],
                            event: event,
                            file: null,
                        });
                    }
                    break;
                case "uploadAll":
                    const files = this.queue.filter(
                        (file) => file.progress.status === UploadStatus.Queue
                    );
                    if (files.length > 0) {
                        event.type = "upload";
                        this.uploadScheduler.next({
                            files: files,
                            event: event,
                            file: null,
                        });
                    }
                    break;
                case "cancel":
                    const id = event.id || null;
                    if (!id) {
                        return;
                    }
                    const fileIndex = this.queue.findIndex((file) => file.id === id);
                    if (fileIndex !== -1) {
                        this.serviceEvents.emit({
                            type: "cancelled",
                            files: [this.queue[fileIndex]],
                            message: FFileUtils.getLocalStr("uploadService.cancelUpload")
                        });
                        this.queue.splice(fileIndex, 1);
                    }
                    break;
                    // case 'cancel':
                    // const id = event.id || null;
                    // if (!id) {
                    //   return;
                    // }
                    // const subs = this.subs.filter(sub => sub.id === id);
                    // subs.forEach(sub => {
                    //   if (sub.sub) {
                    //     sub.sub.unsubscribe();
                    //     const fileIndex = this.queue.findIndex(file => file.id === id);
                    //     if (fileIndex !== -1) {
                    //       this.queue[fileIndex].progress.status = UploadStatus.Cancelled;
                    //       this.serviceEvents.emit({ type: 'cancelled', file: this.queue[fileIndex] });
                    //     }
                    //   }
                    // });
                    break;
                // case 'cancelAll':
                //   this.subs.forEach(sub => {
                //     if (sub.sub) {
                //       sub.sub.unsubscribe();
                //     }

                //     const file = this.queue.find(uploadFile => uploadFile.id === sub.id);
                //     if (file) {
                //       file.progress.status = UploadStatus.Cancelled;
                //       this.serviceEvents.emit({ type: 'cancelled', file: file });
                //     }
                //   });
                //   break;
                case "hide":
                    if (!event.id) {
                        return;
                    }
                    let ids = event.id.split(",");
                    this.queue = this.queue.filter((file) => {
                        let tIndex = ids.findIndex((tId) => tId == file.id);
                        return tIndex > -1 ? false : true;
                    });
                    break;
                case "cancelAll":
                    // 取消，直接从队列中移除，不用修改状态
                    const queueFiles = this.queue.filter(
                        (uploadFile) => uploadFile.progress.status === UploadStatus.Queue
                    );
                    if (queueFiles.length) {
                        this.serviceEvents.emit({
                            type: "cancelled",
                            files: queueFiles,
                            message:FFileUtils.getLocalStr("uploadService.cancelUpload"),
                        });
                        this.queue = this.queue.filter(
                            (uploadFile) => uploadFile.progress.status != UploadStatus.Queue
                        );
                    }
                    break;
                case "remove":
                    if (!event.id) {
                        return;
                    }
                    const removeIndex = this.queue.findIndex(
                        (file) => file.id === event.id
                    );
                    if (removeIndex !== -1) {
                        // 得有个开始删除和已经删除
                        this.queue[removeIndex].progress.status = UploadStatus.Remove;
                        this.uploadScheduler.next({
                            files: [this.queue[removeIndex]],
                            event: event,
                            file: null,
                        });
                    }
                    break;
                case "removeAll":
                    const removeQueueFiles = this.queue.filter(
                        (uploadFile) => uploadFile.progress.status === UploadStatus.Queue
                    );
                    if (removeQueueFiles.length) {
                        this.serviceEvents.emit({
                            type: "cancelled",
                            files: removeQueueFiles,
                            message: FFileUtils.getLocalStr("uploadService.deleteSuccess"),
                        });
                        this.queue = this.queue.filter(
                            (uploadFile) => uploadFile.progress.status != UploadStatus.Queue
                        );
                    }
                    // 正在上传的附件是如何处理
                    // const doneFiles = this.queue.filter(uploadFile => uploadFile.progress.status === UploadStatus.Done);
                    if (this.queue.length) {
                        event.type = "remove";
                        this.queue.map(
                            (item) => (item.progress.status = UploadStatus.Remove)
                        );
                        this.uploadScheduler.next({
                            files: this.queue,
                            event: event,
                            file: null,
                        });
                    }
                    break;
            }
        });
    }

    setExtendServerConfig(extendSer) {
        this.extendServerConfig = extendSer;
    }

    serverMethod(upload: {
        file: UploadFile;
        event: UploadInput;
        files?: UploadFile[];
    }): Observable<UploadOutput> {
        return new Observable((observer) => {
            let sub;
            let ids = upload.files.map((fileItem) => fileItem.id);
            switch (upload.event.type) {
                case 'sliceUpload':
                    sub = this.upload(upload.files, upload.event);
                    break;
                case "upload":
                    sub = this.upload(upload.files, upload.event);
                    break;
                case "multipartUpload":
                    sub = this.multipartUpload(upload.files[0], upload.event);
                    break;
                // case 'uploadAll':
                //   ids = upload.files.map(fileItem => fileItem.id);
                //   sub = this.uploadAll(upload.files, upload.event);
                //   break;
                case "remove":
                    sub = this.remove(upload.files, upload.event);
                    break;
                // case 'removeAll':
                //   ids = upload.files.map(fileItem => fileItem.id);
                //   sub = this.removeAll(upload.files, upload.event);
                //   break;
                default:
                    sub = null;
            }
            if (!sub) {
                return;
            }
            sub.pipe(finalize(() => {
                if (!observer.closed) {
                    observer.complete();
                }
            })).subscribe(
                    (output) => {
                        observer.next(output);
                    },
                    (err) => {
                        observer.next(err);
                    },
                    () => {
                        observer.complete();
                    }
                );
            this.subs.push({ ids: ids, sub: sub, id: "" });
            // switch (upload.event.type) {
            //   case 'uploadAll':
            //     this.subs.push({ ids: ids, sub: sub, id: '' });
            //     break;
            //   case 'removeAll':
            //     this.subs.push({ ids: ids, sub: sub, id: '' });
            //     break;
            //   default:

            // }
        });
    }
    /**
     * 单个文件上传
     * @param file
     * @param event
     */
    // uploadFile(file: UploadFile, event: UploadInput): Observable<UploadOutput> {
    //   const uploadFile = <BlobFile>file.nativeFile;
    //   const uploadIndex = this.queue.findIndex(outFile => outFile.nativeFile === uploadFile);

    //   // 已经被取消的不能被上传
    //   if (this.queue[uploadIndex].progress.status === UploadStatus.Cancelled) {
    //     return new Observable(observer => {
    //       observer.complete();
    //     });
    //   }
    //   // 抛出开始上传的事件
    //   this.serviceEvents.emit({ type: 'start', files: [file] });
    //   return this.uploadServerSer.upload(file, event, this.extendServerConfig);
    // }
    multipartUpload(
        file: UploadFile,
        event: UploadInput
    ): Observable<UploadOutput> {
        // 抛出开始上传的事件
        this.serviceEvents.emit({ type: "start", files: [file] });
        return this.uploadServerSer.multipartUpload(
            file,
            event,
            this.extendServerConfig
        );
    }
    upload(files: UploadFile[], event: UploadInput): Observable<UploadOutput> {
        // 抛出开始上传的事件
        this.serviceEvents.emit({ type: "start", files: files });
        return this.uploadServerSer.upload(files, event, this.extendServerConfig);
    }

    remove(files: UploadFile[], event: UploadInput): Observable<UploadOutput> {
        return this.uploadServerSer.remove(files, event, this.extendServerConfig);
    }
    // removeAll(files: UploadFile[], event: UploadInput): Observable<UploadOutput> {
    //   return this.uploadServerSer.removeAll(files, event, this.extendServerConfig);
    // }
    // 重置
    reset() {
        this.queue = [];
        //this.uploadScheduler = new Subject();
        // this.subs.forEach(sub => {
        //   if (sub.sub) {
        //     sub.sub.unsubscribe();
        //   }
        // });
        this.subs = [];
    }

    // 暂时
    setContentTypes(contentTypes: string[]): void {
        if (typeof contentTypes !== "undefined" && contentTypes instanceof Array) {
            if (contentTypes.find((type: string) => type === "*") !== undefined) {
                this.allowedContentTypes = ["*"];
            } else {
                this.allowedContentTypes = contentTypes;
            }
            return;
        }
        this.allowedContentTypes = ["*"];
    }

    allContentTypesAllowed(): boolean {
        return (
            this.allowedContentTypes.find((type: string) => type === "*") !==
            undefined
        );
    }
    /**
     *
     * @param name
     */
    isContentTypeAllowed(name: string): boolean {
        if (this.allContentTypesAllowed()) {
            return true;
        }
        // 附件没有后缀
        if (name.lastIndexOf(".") < 0) {
            return false;
        }
        let namesuffix = name.substr(name.lastIndexOf("."));
        // 简化计算
        return (
            this.allowedContentTypes.findIndex(
                (item) => item.toLowerCase() == namesuffix.toLowerCase()
            ) > -1
        );
    }
    private isNameAllowed(name:string):boolean{
        if(!this.notAllowedCharsInFileName||this.notAllowedCharsInFileName.length==0){
            return true;
        }
        let purName=name.lastIndexOf(".")>-1? name.substr(0,name.lastIndexOf(".")):name;
         // 简化计算
         return !(
            this.notAllowedCharsInFileName.findIndex(
                (item) => purName.indexOf(item)>-1
            ) > -1
        );
    }

    isFileSizeAllowed(fileSize: number): boolean {
        if (!this.maxFileSize) {
            return true;
        }
        // fileSize是b单位 maxFileSize是MB单位
        return fileSize <= this.maxFileSize * 1024 * 1024; // * 8
    }
}
